import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ShieldAlert, ClipboardCheck } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchCuves, retourAtelier, estBlocageFiliere } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

const th = 'px-2 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500';
const cellInput = 'tabular h-10 w-24 border border-slate-300 bg-white px-2 text-center font-data text-sm outline-none focus:border-slate-900';

const construireLignes = (ofs) =>
  ofs.flatMap((o) =>
    o.lignes
      .filter((l) => l.statut !== 'Trituré' && l.statut !== 'Décompté')
      .map((l) => ({
        cle: `${o.id}|${l.id}`,
        ofId: o.id,
        numero: o.numero,
        decanteur: o.decanteur,
        famille: o.famille === 'lampante' ? 'lampante' : 'noble',
        lot: l.id,
        tiers: l.tiers,
        parcours: l.parcours,
        devenir: l.devenir,
        qualite: l.qualite,
        olives: l.poidsNet,
        sortie: l.parcours === 'client' && l.devenir === 'sortie',
      }))
  );

// Grille de saisie groupée : tous les clients des OF sélectionnés sur une seule page.
// Chaque ligne se valide indépendamment, dans n'importe quel ordre, et reste visible
// en « Validé » une fois enregistrée. Aucun ordre imposé, aucun blocage entre lignes.
export const SaisieGroupeeOf = ({ ofs, onDone }) => {
  const { user } = useAuth();
  const [cuves, setCuves] = useState([]);
  const [saisie, setSaisie] = useState({});
  const [faits, setFaits] = useState({});
  const [encours, setEncours] = useState(null);
  const [lignes, setLignes] = useState(() => construireLignes(ofs));

  useEffect(() => {
    fetchCuves().then(setCuves);
  }, []);

  // Une ligne validée quitte les OF ouverts : la grille conserve les lignes déjà
  // affichées pour que chaque client saisi reste visible, marqué « Validé ».
  useEffect(() => {
    setLignes((prev) => {
      const vues = new Set(prev.map((l) => l.cle));
      const ajouts = construireLignes(ofs).filter((l) => !vues.has(l.cle));
      return ajouts.length ? [...prev, ...ajouts] : prev;
    });
  }, [ofs]);

  const cuvesOk = useMemo(
    () => cuves.filter((c) => !c.virtuelle && c.id !== 'CUVE_SORTIE_DIRECTE' && !c.verrouillee),
    [cuves]
  );
  const maj = (cle, champ, valeur) => setSaisie((s) => ({ ...s, [cle]: { ...s[cle], [champ]: valeur } }));

  const valider = async (ligne, override = false) => {
    const v = saisie[ligne.cle] ?? {};
    const kg = Number(v.huile) || 0;
    if (kg <= 0) return toast.error(`${ligne.lot} : saisissez l’huile produite (kg).`);
    if (!ligne.sortie && !v.cuveId) return toast.error(`${ligne.lot} : choisissez la cuve de destination.`);
    setEncours(ligne.cle);
    const { error } = await retourAtelier(
      ligne.ofId,
      [{ id: ligne.lot, huile: kg, repartition: ligne.sortie ? [] : [{ cuveId: v.cuveId, kg }] }],
      user?.name,
      // Jamais de clôture forcée : l'OF se termine quand TOUS ses lots sont déclarés
      // (indispensable pour les OF à plusieurs passages / plusieurs clients).
      { override, cloturer: false }
    );
    setEncours(null);
    if (error) {
      if (
        !override &&
        estBlocageFiliere(error) &&
        estAdmin(user?.role) &&
        window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)
      )
        return valider(ligne, true);
      return toast.error(error);
    }
    setFaits((f) => ({ ...f, [ligne.cle]: { kg, cuve: cuvesOk.find((c) => c.id === v.cuveId)?.nom ?? 'sortie directe' } }));
    toast.success(`${ligne.tiers} — ${ligne.lot} : ${fmt(kg, 1)} kg enregistrés`);
    onDone?.();
  };

  const restants = lignes.filter((l) => !faits[l.cle]).length;

  return (
    <section data-testid="saisie-groupee" className="border border-slate-200 border-l-4 border-l-emerald-700 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-bold text-slate-900">
            <ClipboardCheck size={16} className="text-emerald-700" /> Saisie groupée — retour d’atelier
          </h2>
          <p className="font-data text-xs text-slate-500">
            {lignes.length} ligne(s) sur {ofs.length} OF · remplissez dans n’importe quel ordre, chaque ligne se valide seule
          </p>
        </div>
        <p data-testid="saisie-groupee-avancement" className="font-data text-xs font-bold text-slate-700">
          {lignes.length - restants} / {lignes.length} validé(s)
          {restants === 0 && lignes.length > 0 && <span className="ml-2 text-emerald-700">— liste épuisée</span>}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50">
            <tr>
              {['', 'OF', 'Lot', 'Client / Fournisseur', 'Qualité', 'Olives (kg)', 'Huile produite (kg)', 'Cuve de destination', 'Rendement', 'État'].map((c) => (
                <th key={c} className={th}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lignes.length === 0 && (
              <tr><td colSpan={10} data-testid="saisie-groupee-vide" className="px-3 py-8 text-center text-sm text-slate-500">Aucun lot en attente de retour sur les OF sélectionnés.</td></tr>
            )}
            {lignes.map((l) => {
              const v = saisie[l.cle] ?? {};
              const fait = faits[l.cle];
              const kg = Number(v.huile) || 0;
              const cuve = cuvesOk.find((c) => c.id === v.cuveId);
              const croisee = cuve && (cuve.famille ?? 'noble') !== l.famille;
              const saturee = cuve && kg > cuve.capacite - cuve.volume;
              return (
                <tr key={l.cle} data-testid={`groupee-ligne-${l.lot}`} className={`text-sm ${fait ? 'bg-slate-100 text-slate-400' : ''}`}>
                  <td className="px-2 py-1.5">
                    {fait ? <CheckCircle2 size={16} className="text-emerald-600" data-testid={`groupee-coche-${l.lot}`} /> : <span className="font-data text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-1.5 font-data text-xs">{l.numero}</td>
                  <td className="px-2 py-1.5 font-data text-xs font-semibold">{l.lot}</td>
                  <td className="px-2 py-1.5 text-xs font-semibold">{l.tiers}</td>
                  <td className="px-2 py-1.5 text-xs">{l.qualite}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs">{fmt(l.olives)}</td>
                  <td className="px-2 py-1.5">
                    {fait ? (
                      <span className="font-data text-xs font-bold text-slate-500">{fmt(fait.kg, 1)}</span>
                    ) : (
                      <input
                        data-testid={`groupee-huile-${l.lot}`}
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="kg"
                        value={v.huile ?? ''}
                        onChange={(e) => maj(l.cle, 'huile', e.target.value)}
                        className={cellInput}
                      />
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {fait ? (
                      <span className="font-data text-xs text-slate-500">{fait.cuve}</span>
                    ) : l.sortie ? (
                      <span data-testid={`groupee-sortie-${l.lot}`} className="font-data text-[11px] text-sky-700">Sortie immédiate — le client repart avec son huile</span>
                    ) : (
                      <select
                        data-testid={`groupee-cuve-${l.lot}`}
                        value={v.cuveId ?? ''}
                        onChange={(e) => maj(l.cle, 'cuveId', e.target.value)}
                        className="h-10 w-64 border border-slate-300 bg-white px-2 font-data text-xs outline-none focus:border-slate-900"
                      >
                        <option value="">— Cuve ({l.famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'}) —</option>
                        {cuvesOk.map((c) => (
                          <option key={c.id} value={c.id}>
                            {`${c.nom} · ${(c.famille ?? 'noble') === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · reste ${Math.round(c.capacite - c.volume)} ${c.unite}`}
                          </option>
                        ))}
                      </select>
                    )}
                    {croisee && !fait && (
                      <span data-testid={`groupee-blocage-${l.lot}`} className="mt-1 flex items-center gap-1 border border-red-600 bg-red-50 px-1.5 py-0.5 font-data text-[10px] font-bold text-red-800">
                        <ShieldAlert size={11} /> Filière croisée interdite
                      </span>
                    )}
                    {saturee && !fait && (
                      <span data-testid={`groupee-capacite-${l.lot}`} className="mt-1 block border border-red-600 bg-red-50 px-1.5 py-0.5 font-data text-[10px] font-bold text-red-800">
                        Capacité insuffisante (reste {fmt(cuve.capacite - cuve.volume)} {cuve.unite})
                      </span>
                    )}
                  </td>
                  <td className="tabular px-2 py-1.5 font-data text-xs font-bold">
                    {(fait ? fait.kg : kg) > 0 && l.olives > 0 ? `${fmt((((fait ? fait.kg : kg) / l.olives) * 100), 2)} %` : '—'}
                  </td>
                  <td className="px-2 py-1.5">
                    {fait ? (
                      <span data-testid={`groupee-valide-${l.lot}`} className="border border-emerald-300 bg-emerald-50 px-2 py-1 font-data text-[10px] font-bold uppercase tracking-[0.06em] text-emerald-700">
                        Validé
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`groupee-valider-${l.lot}`}
                        disabled={encours === l.cle || kg <= 0 || (!l.sortie && !v.cuveId) || saturee}
                        onClick={() => valider(l)}
                        className="h-10 bg-emerald-700 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        Valider
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
